import { MentaError, type MentaErrorCode } from './errors';
import { Emitter, type Unsubscribe } from './emitter';
import { buildIframeSrc, createIframeElement, postToIframe } from './transport/iframe';
import type { FieldStyleByState, IframeTheme } from './transport/iframe';
import { MessageRouter } from './transport/message-router';
import type { CardErrorCode, FieldName, ParentInboundMessage } from '../protocol';

export type GroupPhase = 'idle' | 'mounting' | 'ready' | 'tokenizing' | 'destroyed';

export interface FieldGroupMountOptions {
  token: string;
  theme?: IframeTheme;
  locale?: string;
  style?: FieldStyleByState;
}

export interface FieldGroupEvents {
  ready: () => void;
  error: (err: { code: MentaErrorCode; reason?: string }) => void;
  /** Per-field validity, sourced from the coordinator's `field-state`
   * message (the coordinator is the only party that can observe every
   * field's validity, since raw values never leave the same-origin
   * BroadcastChannel — see engram sdd/split-secure-fields-per-input).
   * `CardFieldHandle` subscribes here (filtered by its own field name)
   * instead of on its own `CardFieldSession`, which never receives
   * `field-state` because that message's `event.source` is the
   * coordinator iframe, not the individual field iframe. */
  fieldValid: (field: FieldName, valid: boolean, code?: CardErrorCode) => void;
  /** Card-preview signal (Engram odd/card-preview/tasks), sourced the same
   * way as `fieldValid` above — from the coordinator's `field-input`
   * message, since raw card values never leave the same-origin
   * BroadcastChannel and only a digit count (+ pan-only BIN) crosses to
   * the parent. `CardFieldHandle` subscribes here, filtered by its own
   * field name. */
  fieldInput: (field: FieldName, length: number, bin?: string) => void;
  /** Focus/blur signal (Engram odd/sdk-options/tasks, item 3), sourced from
   * the coordinator's `field-focus` message, same relay path as
   * `fieldValid`/`fieldInput` above. `CardFieldHandle` subscribes here,
   * filtered by its own field name and `focused` polarity. */
  fieldFocus: (field: FieldName, focused: boolean) => void;
  /** The hello handshake hit `HELLO_TIMEOUT_MS` without `group-ready`.
   * Emitted once per coordinator document; `CardFieldHandle` relays it to
   * its own `error` listeners as `{ code: 'handshake_failed' }`. Separate
   * from `error` (token failures), which is not relayed to every handle. */
  handshakeFailed: () => void;
}

const TOKEN_TIMEOUT_MS = 30000;
/** H3/H4 contract point 4: fixed re-send interval for `parent-hello`. */
const HELLO_INTERVAL_MS = 500;
/** No pre-existing group-level handshake timeout existed before this
 * change; pinned to match the coordinator's own 8s group timeout and
 * `CardFieldSession.LOAD_TIMEOUT_MS` (orchestrator decision, task doc). */
const HELLO_TIMEOUT_MS = 8000;

/** Wire `CardErrorCode` -> public `MentaErrorCode`, same mapping
 * `CardFieldSession` used pre-split — moved here with `createCardToken()`
 * itself (design: "`createCardToken()` moves here from `session.ts`").
 * Exported so `CardFieldHandle` can map a per-field `field-state` error
 * code the same way. */
export function mapWireErrorCode(code: CardErrorCode | undefined): MentaErrorCode {
  switch (code) {
    case 'invalid_card_number':
    case 'invalid_expiry':
    case 'card_expired':
    case 'invalid_cvv':
    case 'invalid_card_details':
      return 'invalid_card';
    case 'network_error':
      return 'network_error';
    case 'request_rejected':
    case 'service_error':
    case 'malformed_response':
    case 'unknown_error':
    case 'iframe_load_timeout':
    case undefined:
      return 'unknown';
    default:
      return 'unknown';
  }
}

interface PendingToken {
  promise: Promise<{ cardReferenceId: string }>;
  resolve: (value: { cardReferenceId: string }) => void;
  reject: (reason: MentaError) => void;
}

/** `FieldGroup` — owns the 4th, headless coordinator iframe (design D1) and
 * the GROUP-level tokenization lifecycle (idle -> mounting -> ready ->
 * tokenizing -> destroyed): single-flight `createCardToken()`, the 30s token
 * deadline, and `group-ready` aggregation (design D3). Mounted lazily by
 * `FieldsController` on the first `create()` call in its group — never by
 * merchant code directly (spec: "no merchant code mounts the coordinator
 * iframe directly"). */
export class FieldGroup {
  private currentPhase: GroupPhase = 'idle';
  private iframe: HTMLIFrameElement | undefined;
  private pending: PendingToken | undefined;
  private tokenTimer: ReturnType<typeof setTimeout> | undefined;
  private helloInterval: ReturnType<typeof setInterval> | undefined;
  private helloTimeout: ReturnType<typeof setTimeout> | undefined;
  private readonly emitter = new Emitter<FieldGroupEvents>();

  constructor(
    private readonly origin: string,
    private readonly router: MessageRouter,
    private readonly groupId: string,
    private readonly siteKey: string,
  ) {}

  get phase(): GroupPhase {
    return this.currentPhase;
  }

  on<K extends keyof FieldGroupEvents>(event: K, callback: FieldGroupEvents[K]): Unsubscribe {
    return this.emitter.on(event, callback as never);
  }

  /** Idempotent: mounts the headless coordinator iframe only on the first
   * call while `idle` (spec: "a second `create(...)` call in the same
   * group does not mount a second coordinator"). */
  ensureMounted(options: FieldGroupMountOptions): void {
    if (this.currentPhase !== 'idle') return;
    this.currentPhase = 'mounting';

    const src = buildIframeSrc(this.origin, {
      route: 'coordinator',
      group: this.groupId,
      token: options.token,
      siteKey: this.siteKey,
      nonce: Date.now(),
      theme: options.theme,
      locale: options.locale,
    });
    const iframe = createIframeElement(src, { headless: true });
    document.body.appendChild(iframe);
    this.iframe = iframe;

    iframe.addEventListener('load', () => this.startHelloHandshake());

    if (iframe.contentWindow) {
      this.router.register(this.origin, iframe.contentWindow, (message) => this.handleMessage(message));
    }
  }

  /** H3/H4 contract point 4: on coordinator iframe `load`, post
   * `{type: 'parent-hello'}` and re-send every `HELLO_INTERVAL_MS` until
   * `group-ready` arrives or `HELLO_TIMEOUT_MS` elapses, whichever is
   * first. */
  private startHelloHandshake(): void {
    if (!this.iframe) return;
    // A `load` firing again (coordinator re-navigation/reload) means a new
    // document — it needs a fresh handshake, so clear any handshake still
    // running for the previous document before starting a new one; without
    // this, `helloInterval`/`helloTimeout` are overwritten and the previous
    // interval leaks forever (unreachable by `stopHelloHandshake()`).
    this.stopHelloHandshake();
    this.sendHello();
    this.helloInterval = setInterval(() => this.sendHello(), HELLO_INTERVAL_MS);
    this.helloTimeout = setTimeout(() => this.handleHelloTimeout(), HELLO_TIMEOUT_MS);
  }

  /** Handshake gave up without `group-ready`. The coordinator stays silent
   * to unauthorized origins by design, so this is the only signal the
   * integrator can get; it is intentionally reason-free. */
  private handleHelloTimeout(): void {
    this.stopHelloHandshake();
    if (this.currentPhase !== 'mounting') return;
    this.emitter.emit('handshakeFailed');
  }

  private sendHello(): void {
    if (!this.iframe) return;
    postToIframe(this.iframe, { type: 'parent-hello' }, this.origin);
  }

  private stopHelloHandshake(): void {
    if (this.helloInterval) clearInterval(this.helloInterval);
    if (this.helloTimeout) clearTimeout(this.helloTimeout);
    this.helloInterval = undefined;
    this.helloTimeout = undefined;
  }

  /** Sends a `field-command` to the coordinator (Engram odd/sdk-options/tasks,
   * item B) — a no-op if the coordinator iframe is not mounted yet (e.g.
   * called before the group's first `create()`, which can't actually
   * happen from `CardFieldHandle` since it always holds a `FieldGroup` that
   * already went through `ensureMounted()`). This method only transports;
   * it does not validate anything itself. */
  sendFieldCommand(field: FieldName, command: 'focus' | 'clear'): void {
    if (!this.iframe) return;
    postToIframe(this.iframe, { type: 'field-command', field, command }, this.origin);
  }

  createCardToken(): Promise<{ cardReferenceId: string }> {
    if (this.currentPhase === 'tokenizing' && this.pending) {
      return this.pending.promise;
    }

    if (this.currentPhase !== 'ready') {
      return Promise.reject(new MentaError('not_ready', 'Field group is not ready to tokenize'));
    }

    let resolve!: PendingToken['resolve'];
    let reject!: PendingToken['reject'];
    const promise = new Promise<{ cardReferenceId: string }>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    this.pending = { promise, resolve, reject };
    this.currentPhase = 'tokenizing';

    if (this.iframe) {
      postToIframe(this.iframe, { type: 'submit-card' }, this.origin);
    }

    this.tokenTimer = setTimeout(() => this.handleTokenTimeout(), TOKEN_TIMEOUT_MS);

    return promise;
  }

  unmount(): void {
    if (this.currentPhase === 'destroyed') {
      return;
    }

    if (this.tokenTimer) clearTimeout(this.tokenTimer);
    this.stopHelloHandshake();
    if (this.iframe?.contentWindow) {
      this.router.unregister(this.iframe.contentWindow);
    }
    this.iframe?.remove();
    this.iframe = undefined;
    this.currentPhase = 'destroyed';

    if (this.pending) {
      this.pending.reject(new MentaError('not_ready', 'Field group was unmounted'));
      this.pending = undefined;
    }
  }

  private handleMessage(message: ParentInboundMessage): void {
    if (message.type === 'group-ready') {
      this.transitionToReady();
      return;
    }

    if (message.type === 'card-ready') {
      this.settleTokenSuccess(message.card_reference_id);
      return;
    }

    if (message.type === 'card-error') {
      this.settleTokenFailure(message.code, message.reason);
      return;
    }

    if (message.type === 'field-state') {
      this.emitter.emit('fieldValid', message.field, message.valid, message.code);
      return;
    }

    // `length` is optional on the permissive consumer type (tolerating a
    // hypothetical older iframe build), so a message missing it is dropped
    // rather than defaulting to 0 and reporting a wrong digit count.
    if (message.type === 'field-input' && typeof message.length === 'number') {
      this.emitter.emit('fieldInput', message.field, message.length, message.bin);
      return;
    }

    if (message.type === 'field-focus') {
      this.emitter.emit('fieldFocus', message.field, message.focused);
    }
  }

  private transitionToReady(): void {
    if (this.currentPhase === 'destroyed') return;
    this.stopHelloHandshake();
    this.currentPhase = 'ready';
    this.emitter.emit('ready');
  }

  private handleTokenTimeout(): void {
    if (this.currentPhase !== 'tokenizing' || !this.pending) return;
    const pending = this.pending;
    this.pending = undefined;
    this.currentPhase = 'ready';
    pending.reject(new MentaError('timeout', 'createCardToken() timed out waiting for a reply'));
  }

  private settleTokenSuccess(cardReferenceId: string): void {
    if (this.currentPhase !== 'tokenizing' || !this.pending) return;
    if (this.tokenTimer) clearTimeout(this.tokenTimer);
    const pending = this.pending;
    this.pending = undefined;
    this.currentPhase = 'ready';
    pending.resolve({ cardReferenceId });
  }

  private settleTokenFailure(code: CardErrorCode | undefined, reason: string | undefined): void {
    if (this.currentPhase !== 'tokenizing' || !this.pending) return;
    if (this.tokenTimer) clearTimeout(this.tokenTimer);
    const pending = this.pending;
    this.pending = undefined;
    this.currentPhase = 'ready';
    this.emitter.emit('error', reason !== undefined ? { code: mapWireErrorCode(code), reason } : { code: mapWireErrorCode(code) });
    pending.reject(new MentaError(mapWireErrorCode(code), 'card tokenization failed'));
  }
}
